import { useMemo } from "react";

import { useLocation } from "@tanstack/react-router";
import { observer } from "mobx-react-lite";

import { IconArchive, IconFileListReport, IconFolderOpen, IconFolderProjects, IconQuestion } from "@synthesis/shared-icons";
import { Navbar, type Group, type GroupItem } from "@synthesis/shared-ui";

import { useQueryProject } from "../../../entity/project";
import { useUserRolesGGE } from "../../hooks";

const linksWhichBlockSideBar = ["/projects/add-project"];

export const SideBar = observer(() => {
  const location = useLocation();
  const match = location.pathname.includes("project")
    ? location.pathname.match(/\/project\/([^/]+)/)
    : location.pathname.match(/\/archiveProject\/([^/]+)/);
  const projectId = match ? match[1] : "";
  const { project, isLoading } = useQueryProject(projectId ?? "") || { project: null, isLoading: false };
  const roles = useUserRolesGGE();

  const handleGetIsUniqLink = (field: GroupItem | Group) => location.pathname === field?.link;

  const navBarGroups: Group[] = useMemo(() => {
    const isAllowedRole = roles.АДМИНИСТРИРОВАНИЕ || roles.МОДЕРИРОВАНИЕ || roles.СУПЕР;
    const isReportsAllowed = roles.АДМИНИСТРИРОВАНИЕ || roles.СУПЕР;

    const projectItems = [
      { id: "project_item_1", name: "Карточка проекта", link: `/project/${project?.id}/dashboard` },
      { id: "project_item_2", name: "Трехмерные модели", link: `/project/${project?.id}/filesystem/models` },
      { id: "project_item_3", name: "Документация", link: `/project/${project?.id}/filesystem/docs` },
      { id: "project_item_5", name: "Коллекция чертежей", link: `/project/${project?.id}/filesystem/drawings` },
    ];

    if (isAllowedRole) {
      projectItems.push({ id: "project_item_4", name: "Список экспертов", link: `/project/${project?.id}/role` });
    }

    const archiveItems = [
      { id: "archive_item_5", name: "Карточка проекта", link: `/archiveProject/${project?.id}/dashboard` },
      { id: "archive_item_6", name: "Трехмерные модели", link: `/archiveProject/${project?.id}/filesystem/models` },
      { id: "archive_item_8", name: "Документация", link: `/archiveProject/${project?.id}/filesystem/docs` },
      { id: "archive_item_9", name: "Коллекция чертежей", link: `/archiveProject/${project?.id}/filesystem/drawings` },
    ];

    if (isAllowedRole) {
      archiveItems.push({ id: "archive_item_7", name: "Список экспертов", link: `/archiveProject/${project?.id}/role` });
    }

    const groups: Group[] = [
      {
        id: "group_1",
        label: "Проекты",
        description: "description",
        link: "/",
        icon: <IconFolderProjects className="size-4" />,
        openIcon: <IconFolderOpen className="size-4" />,
        itemsStartsWith: "project",
        descriptionItems: project?.title,
        items: projectItems,
      },
      {
        id: "group_2",
        label: "Архив проектов",
        link: "/archivedProjects",
        icon: <IconArchive className="size-4" />,
        openIcon: <IconFolderOpen className="size-4" />,
        itemsStartsWith: "archiveProject",
        description: "description2",
        descriptionItems: project?.title,
        items: archiveItems,
      },
    ];

    if (isReportsAllowed) {
      groups.push({
        id: "reports-1",
        label: "Отчеты",
        icon: <IconFileListReport className="size-4" />,
        link: "/reports",
        itemsStartsWith: "reports",
        description: "description3",
      });
    }

    groups.push({
      id: "help-1",
      label: "Помощь",
      icon: <IconQuestion className="size-4" />,
      link: "/help",
      itemsStartsWith: "/help",
      description: "description3",
      items: [
        {
          id: "help_1",
          name: "Руководство пользователя (.pdf)",
          href: "/Платформа3D. Руководство пользователя.pdf",
          nonTruncate: true,
        },
        {
          id: "help_2",
          name: "Портал самообслуживания пользователей",
          link: "http://support.gge.ru/",
          isOpenInNewTab: true,
          nonTruncate: true,
        },
      ],
    });

    return groups;
  }, [project, roles]);

  return (
    <Navbar
      id="sidebar"
      isLoadingDescription={isLoading}
      groups={navBarGroups}
      linksWhichBlockSideBar={linksWhichBlockSideBar}
      handleGetIsUniqLink={handleGetIsUniqLink}
    />
  );
});
